import React, { useEffect, useState } from "react";
import { Button, Dropdown, Menu, Tooltip } from "antd";
import { GlobalOutlined } from "@ant-design/icons";
import "./index.css";
import { useTranslation } from "react-i18next";

type FontSize = "small" | "default" | "large";

interface AccessibilityControlsProps {
  showLanguage?: boolean;
}

const AccessibilityControls: React.FC<AccessibilityControlsProps> = ({
  showLanguage = true,
}) => {
  const [fontSize, setFontSize] = useState<FontSize>("default");
  const [highContrast, setHighContrast] = useState(false);

  const { i18n } = useTranslation();

    const [language, setLanguage] = useState(
     localStorage.getItem("i18nextLng") || "en"
    );

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
        setLanguage(lng);
    };

  useEffect(() => {
    const savedFontSize =
      (localStorage.getItem("aa_font_size") as FontSize) || "default";

    const savedContrast =
      localStorage.getItem("aa_high_contrast") === "true";

    setFontSize(savedFontSize);
    setHighContrast(savedContrast);

    document.documentElement.classList.remove(
      "aa-font-small",
      "aa-font-default",
      "aa-font-large",
      "aa-high-contrast"
    );

    document.documentElement.classList.add(`aa-font-${savedFontSize}`);

    if (savedContrast) {
      document.documentElement.classList.add("aa-high-contrast");
    }
  }, []);

  const changeFontSize = (size: FontSize) => {
    setFontSize(size);

    localStorage.setItem("aa_font_size", size);

    document.documentElement.classList.remove(
      "aa-font-small",
      "aa-font-default",
      "aa-font-large"
    );

    document.documentElement.classList.add(`aa-font-${size}`);
  };

  const toggleContrast = () => {
    const newValue = !highContrast;

    setHighContrast(newValue);
    localStorage.setItem("aa_high_contrast", String(newValue));

    document.documentElement.classList.toggle(
      "aa-high-contrast",
      newValue
    );
  };

  const languageMenu = (
    <Menu
        selectedKeys={[language]}
        onClick={({ key }) => changeLanguage(String(key))}
    >
        <Menu.Item key="en">
        English
        </Menu.Item>

        <Menu.Item key="hi">
        हिंदी
        </Menu.Item>

        <Menu.Item key="mr">
        मराठी
        </Menu.Item>
    </Menu>
    );

  return (
    <div className="accessibilityControls">

      {/* FIRST ROW */}
      <div>
        <Tooltip title="Decrease text size">
          <Button
            type="text"
            className={`fontControl ${
              fontSize === "small" ? "active" : ""
            }`}
            onClick={() => changeFontSize("small")}
          >
            A<sup>−</sup>
          </Button>
        </Tooltip>

        <Tooltip title="Default text size">
          <Button
            type="text"
            className={`fontControl ${
              fontSize === "default" ? "active" : ""
            }`}
            onClick={() => changeFontSize("default")}
          >
            A
          </Button>
        </Tooltip>

        <Tooltip title="Increase text size">
          <Button
            type="text"
            className={`fontControl ${
              fontSize === "large" ? "active" : ""
            }`}
            onClick={() => changeFontSize("large")}
          >
            A<sup>+</sup>
          </Button>
        </Tooltip>

        <Tooltip
          title={
            highContrast
              ? "Turn off high contrast"
              : "Turn on high contrast"
          }
        >
          <Button
            type="text"
            className={`contrastControl ${
              highContrast ? "active" : ""
            }`}
            onClick={toggleContrast}
          >
            ◐
          </Button>
        </Tooltip>
      </div>


      {/* SECOND ROW */}
      {showLanguage && (
        <div>
          <div className="accessibilityDivider" />

          <Dropdown overlay={languageMenu} trigger={["click"]}>
            <Button
              type="text"
              className="languageControl"
              aria-label="Select language"
            >
              <GlobalOutlined />
              <span>English</span>
              <span className="languageArrow">⌄</span>
            </Button>
          </Dropdown>
        </div>
      )}

    </div>
  );
};

export default AccessibilityControls;